import { Loader2, Plus, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  asOutline,
  canIndent,
  canMove,
  canOutdent,
  durationInWeeks,
  hasChildren,
  moveRow,
  outlineNumbers,
  removeRow,
  shiftRow,
  subPhaseSpan,
  subtreeEnd,
  type ParsedPhase,
  type PhaseInput,
} from "@gb/contracts";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PhaseCard, type PhaseDraft } from "../phase-card";
import { useFoldedGroups } from "../phase-outline";
import { usePhases, useParsePlan, useReplacePhases } from "../phases-api";
import { useUploadPlan } from "../documents-api";
import { useSetSetupStep, useUpdateProject } from "../api";
import { Field } from "@/components/gb/field";
import { STEP, StepPanel, WizardFooter, type StepProps } from "./wizard";

/** Add a day to an ISO date, for the "next phase starts after this one" default. */
function dayAfter(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** A key for a row the server has not seen yet. */
let drafts = 0;
const draftKey = (): string => `draft-${(drafts += 1)}`;
const keyOf = (phase: PhaseDraft): string => phase.key;

const emptyPhase = (startDate = ""): PhaseDraft => ({
  key: draftKey(),
  level: 0,
  name: "",
  startDate,
  endDate: "",
  note: "",
  origin: "manual",
  editedByUser: false,
});

function toDraft(parsed: ParsedPhase, sourceFilename: string, sourceDocumentId: string): PhaseDraft {
  return {
    key: draftKey(),
    level: parsed.level,
    name: parsed.name,
    startDate: parsed.startDate ?? "",
    endDate: parsed.endDate ?? "",
    note: parsed.note ?? "",
    origin: "parsed",
    editedByUser: false,
    sourceFilename,
    sourceDocumentId,
  };
}

/**
 * Step 2 — milestones.
 *
 * Continue is never blocked here: zero phases is valid, and a plan that cannot
 * be read still leaves its document stored.
 */
export function StepMilestones({ project, mode, goTo }: StepProps) {
  const projectId = project?.id;
  const { data: serverPhases, isPending } = usePhases(projectId);

  const [startDate, setStartDate] = useState(project?.startDate ?? "");
  const [handoverDate, setHandoverDate] = useState(project?.targetHandoverDate ?? "");
  const [phases, setPhases] = useState<PhaseDraft[]>([]);
  // By the row's key, not its place: a row moved or removed after Continue
  // took a message by place onto whichever row then had that place.
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dateError, setDateError] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [merge, setMerge] = useState<{ parsed: PhaseDraft[] } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const hydrated = useRef(false);

  const updateProject = useUpdateProject();
  const replacePhases = useReplacePhases(projectId ?? "");
  const setStep = useSetSetupStep();
  const uploadPlan = useUploadPlan();
  const parsePlan = useParsePlan();

  // Hydrate the form once, from the server. Re-running on every query settle
  // would overwrite whatever the user has typed since.
  useEffect(() => {
    if (hydrated.current || !serverPhases) return;
    hydrated.current = true;
    setPhases(
      serverPhases.map((phase) => ({
        id: phase.id,
        key: phase.id,
        level: phase.level,
        name: phase.name,
        startDate: phase.startDate ?? "",
        endDate: phase.endDate ?? "",
        note: phase.note ?? "",
        origin: phase.origin,
        editedByUser: phase.editedByUser,
        sourceDocumentId: phase.sourceDocumentId ?? undefined,
      })),
    );
  }, [serverPhases]);

  // Read-only and recomputed on every change — never an input. A typed
  // duration and typed dates disagree the moment either changes, and there is
  // no correct way to decide which the user meant.
  const weeks = durationInWeeks(startDate || null, handoverDate || null);

  const busy =
    updateProject.isPending || replacePhases.isPending || setStep.isPending;
  const planBusy = uploadPlan.isPending || parsePlan.isPending;

  const patchPhase = (index: number, patch: Partial<PhaseDraft>): void =>
    setPhases((current) =>
      current.map((phase, i) => (i === index ? { ...phase, ...patch } : phase)),
    );

  // A phase moves with its sub-phases, and taking one out lifts its
  // sub-phases into its place (`phase-outline.ts`).
  const movePhase = (index: number, direction: -1 | 1): void =>
    setPhases((current) => moveRow(current, index, direction));

  const shiftPhase = (index: number, by: -1 | 1): void =>
    setPhases((current) => shiftRow(current, index, by));

  const outline = useFoldedGroups(phases, keyOf);
  const numbers = outlineNumbers(phases);
  const subPhases = phases.filter((phase) => phase.level > 0).length;

  const addPhase = (): void =>
    setPhases((current) => {
      const previousEnd = current[current.length - 1]?.endDate;
      return [...current, emptyPhase(previousEnd ? dayAfter(previousEnd) : "")];
    });

  /**
   * Upload the plan, then parse it.
   *
   * The document is stored *first* and forced to `programme`, so a failed parse
   * still leaves the file in place and in Brain → Sources — storing the original
   * is the hard requirement here, not the parse.
   *
   * The parse also returns a project suggestion, which this step ignores: by
   * the time anyone is here the basics are filled in and step 1 offered to read
   * them. Overwriting a name the user has already confirmed because they later
   * imported a revised programme would be the opposite of helpful.
   */
  const onPlanFile = async (file: File): Promise<void> => {
    if (!projectId) return;
    setPlanError(null);
    try {
      const document = await uploadPlan.mutateAsync({ projectId, file });
      const result = await parsePlan.mutateAsync({ projectId, documentId: document.id });

      if (result.phases.length === 0) {
        setPlanError(
          "Nothing recognisable in that file — add phases manually. The document is saved either way.",
        );
        return;
      }

      const drafts = result.phases.map((phase) =>
        toDraft(phase, document.filename, document.id),
      );

      // Losing typed work to a background job is the worst failure mode in this
      // step, so anything already entered forces an explicit choice.
      if (phases.some((phase) => phase.name.trim())) {
        setMerge({ parsed: drafts });
      } else {
        setPhases(drafts);
      }
    } catch (error) {
      setPlanError(error instanceof Error ? error.message : "Couldn’t read that plan.");
    }
  };

  /**
   * Write the dates and phases as they stand. False means nothing was sent.
   */
  const persist = async (): Promise<boolean> => {
    setErrors({});
    setDateError(null);

    if (startDate && handoverDate && handoverDate <= startDate) {
      setDateError("Target handover must be after the project start date.");
      return false;
    }

    const nextErrors: Record<string, string> = {};
    const withErrors: number[] = [];
    phases.forEach((phase, index) => {
      const error = !phase.name.trim()
        ? "A phase needs a name."
        : phase.startDate && phase.endDate && phase.endDate < phase.startDate
          ? "End date cannot be before the start date."
          : null;
      if (!error) return;
      nextErrors[phase.key] = error;
      withErrors.push(index);
    });
    if (withErrors.length > 0) {
      setErrors(nextErrors);
      // A row with an error may be inside a folded group.
      outline.reveal(withErrors);
      return false;
    }

    if (!project) return false;

    // **Only when there is something to say.** Both dates are optional — the
    // step says so at the top — and an empty patch is refused by
    // `ProjectBasicsPatchSchema` with "No fields to update". That surfaced as a
    // red error on a step a user had deliberately left blank, telling them
    // nothing about what to do and blocking Continue on a screen that had just
    // promised they could skip it.
    const patch = {
      ...(startDate ? { startDate } : {}),
      ...(handoverDate ? { targetHandoverDate: handoverDate } : {}),
    };
    if (Object.keys(patch).length > 0) {
      await updateProject.mutateAsync({ id: project.id, patch });
    }

    const payload: PhaseInput[] = asOutline(phases).map((phase) => ({
      ...(phase.id ? { id: phase.id } : {}),
      level: phase.level,
      name: phase.name.trim(),
      startDate: phase.startDate || null,
      endDate: phase.endDate || null,
      note: phase.note.trim() || null,
      sourceDocumentId: phase.sourceDocumentId ?? null,
      origin: phase.origin,
      editedByUser: phase.editedByUser,
    }));
    await replacePhases.mutateAsync(payload);
    return true;
  };

  const submit = async (): Promise<void> => {
    if (!(await persist())) return;
    // Skipped in edit mode: the cursor is monotonic and a live project is
    // already at the last step, so the call would be a no-op round trip.
    if (mode === "setup") {
      await setStep.mutateAsync({ id: project!.id, step: STEP.compliance });
    }
    goTo(STEP.compliance);
  };

  /**
   * Back commits, the same as Continue — see the note in `step-compliance.tsx`.
   * A parse the user has just spent a minute correcting is exactly the kind of
   * work that used to disappear on the way back to Basics.
   */
  const back = async (): Promise<void> => {
    await persist();
    goTo(STEP.basics);
  };

  const failure =
    updateProject.error ?? replacePhases.error ?? setStep.error ?? null;

  return (
    <>
      <StepPanel
        title="Milestones"
        description="When the job runs, and the phases it breaks into. Everything here is optional — you can come back once the programme is agreed."
      >
        <div className="flex flex-col gap-7">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Project start" error={dateError ?? undefined}>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Target handover">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={handoverDate}
                  onChange={(e) => setHandoverDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Duration" hint="Calculated from the dates.">
              {(props) => (
                <Input
                  {...props}
                  readOnly
                  tabIndex={-1}
                  value={weeks === null ? "" : `${weeks} weeks`}
                  placeholder="—"
                  className="bg-subtle text-muted"
                />
              )}
            </Field>
          </div>

          <section>
            {/* The slim counterpart of step 1's box, which is where a plan is
                normally uploaded now — that one offers to fill in the whole
                project, this one only ever touches the phases below it.

                It stays because step 2 is also reachable in edit mode on a live
                project, and step 1's create-time flow is not: without this,
                importing a Rev D programme into a running site would mean
                typing every phase by hand. */}
            <div className="flex flex-wrap items-center gap-4 rounded-card border-2 border-dashed border-border bg-surface-sunken px-5 py-4">
              <span className="flex shrink-0 text-muted">
                <Upload size={24} aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[1rem] font-bold text-ink">
                  Import phases from a programme.
                </p>
                <p className="mt-0.5 text-[0.84rem] text-muted">
                  PDF / DOCX / XLSX — phase names and dates get filled in below
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => fileRef.current?.click()}
                disabled={planBusy || !projectId}
                className="shrink-0"
              >
                {planBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                {planBusy ? "Reading plan…" : "Choose file"}
              </Button>
              <input
                ref={fileRef}
                type="file"
                className="sr-only"
                accept=".pdf,.docx,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void onPlanFile(file);
                }}
              />
            </div>
            {planError && (
              <p
                role="status"
                className="mt-2.5 rounded-input border-[1.5px] border-border bg-surface px-4 py-3 text-[0.83rem] text-ink-slate"
              >
                {planError}
              </p>
            )}
          </section>

          <section>
            <SectionLabel
              className="mb-3"
              trailing={
                subPhases > 0
                  ? `${phases.length - subPhases} ${phases.length - subPhases === 1 ? "phase" : "phases"} · ${subPhases} ${subPhases === 1 ? "sub-phase" : "sub-phases"}`
                  : phases.length === 1
                    ? "1 phase"
                    : `${phases.length} phases`
              }
            >
              Phases
            </SectionLabel>

            {isPending ? (
              <div className="flex items-center gap-2.5 py-6 text-muted" role="status">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[0.88rem] font-semibold">Loading phases…</span>
              </div>
            ) : phases.length === 0 ? (
              <p className="rounded-card border-[1.5px] border-border bg-surface px-4 py-6 text-center text-[0.88rem] text-muted">
                No phases yet. Add them by hand, upload a programme, or skip this
                entirely — it doesn’t block setup.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {outline.visible.map((index) => {
                  const phase = phases[index]!;
                  return (
                    <PhaseCard
                      key={phase.key}
                      phase={phase}
                      number={numbers[index]!}
                      error={errors[phase.key]}
                      canMoveUp={canMove(phases, index, -1)}
                      canMoveDown={canMove(phases, index, 1)}
                      canIndent={canIndent(phases, index)}
                      canOutdent={canOutdent(phases, index)}
                      group={
                        hasChildren(phases, index)
                          ? {
                              count: subtreeEnd(phases, index) - index - 1,
                              folded: outline.isFolded(phase),
                              span: subPhaseSpan(phases, index),
                              onToggle: () => outline.toggle(phase.key),
                            }
                          : undefined
                      }
                      onChange={(patch) => patchPhase(index, patch)}
                      onMove={(direction) => movePhase(index, direction)}
                      onShift={(by) => shiftPhase(index, by)}
                      onRemove={() => setPhases((current) => removeRow(current, index))}
                    />
                  );
                })}
              </ul>
            )}

            <Button variant="secondary" className="mt-3" onClick={addPhase}>
              <Plus size={16} />
              Add phase
            </Button>
          </section>
        </div>
      </StepPanel>

      <MergeDialog
        pending={merge}
        existingCount={phases.filter((p) => p.name.trim()).length}
        onReplace={() => {
          setPhases(merge!.parsed);
          setMerge(null);
        }}
        onAppend={() => {
          setPhases((current) => [...current, ...merge!.parsed]);
          setMerge(null);
        }}
        onCancel={() => setMerge(null)}
      />

      <WizardFooter
        onBack={() => void back()}
        onContinue={() => void submit()}
        busy={busy}
        error={failure instanceof Error ? failure.message : null}
      />
    </>
  );
}

/**
 * "Replace your 3 phases with the 6 from this plan, or add them below?"
 *
 * The whole point of this step's design: a background parse must never silently
 * discard what the user typed.
 */
function MergeDialog({
  pending,
  existingCount,
  onReplace,
  onAppend,
  onCancel,
}: {
  pending: { parsed: PhaseDraft[] } | null;
  existingCount: number;
  onReplace: () => void;
  onAppend: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle>That plan has {pending?.parsed.length ?? 0} phases</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>
            You’ve already entered {existingCount}{" "}
            {existingCount === 1 ? "phase" : "phases"}. Replace them with the ones
            from this plan, or add the new ones below what you have?
          </DialogDescription>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="footer" onClick={onCancel}>
            Keep mine
          </Button>
          <Button variant="secondary" size="footer" onClick={onAppend}>
            Add below
          </Button>
          <Button size="footer" onClick={onReplace}>
            Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
